import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, X, ChevronLeft, ChevronRight, Truck, RotateCcw, ShieldCheck, Ruler, Lock } from "lucide-react";
import api, { mediaUrl, formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import ProductCard from "@/components/ProductCard";
import useSeo, { CANONICAL_BASE } from "@/lib/useSeo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

function ReviewForm({ productId, onDone }) {
  const [form, setForm] = useState({ rating: 5, title: "", text: "", name: "", email: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/reviews", { product_id: productId, ...form });
      toast.success(data.message);
      setForm({ rating: 5, title: "", text: "", name: "", email: "" });
      onDone?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <form onSubmit={submit} className="space-y-3 max-w-lg" data-testid="review-form">
      <div className="flex gap-1">{[1, 2, 3, 4, 5].map((n) => (
        <button type="button" key={n} onClick={() => setForm({ ...form, rating: n })}><Star size={20} fill={n <= form.rating ? "#1D1D1B" : "none"} /></button>
      ))}</div>
      <input required placeholder="Titel" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border border-[#E8E1D8] px-3 py-2 text-sm bg-white" data-testid="review-title" />
      <textarea required placeholder="Jouw review" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} className="w-full border border-[#E8E1D8] px-3 py-2 text-sm bg-white" rows={3} data-testid="review-text" />
      <div className="grid grid-cols-2 gap-3">
        <input required placeholder="Naam" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-[#E8E1D8] px-3 py-2 text-sm bg-white" data-testid="review-name" />
        <input required type="email" placeholder="E-mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="border border-[#E8E1D8] px-3 py-2 text-sm bg-white" data-testid="review-email" />
      </div>
      <button className="btn-x-outline" data-testid="review-submit">REVIEW PLAATSEN</button>
    </form>
  );
}

export default function Product() {
  const { slug } = useParams();
  const { addItem } = useCart();
  const { settings } = useSettings();
  const [p, setP] = useState(null);
  const [active, setActive] = useState(0);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [nf, setNf] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);

  const load = () => api.get(`/products/${slug}`).then(({ data }) => setP(data)).catch(() => setNf(true));
  useEffect(() => { setP(null); setSize(null); setActive(0); setColor(null); setNf(false); load(); }, [slug]);

  useSeo(p ? {
    title: p.seo_title || `${p.name} — ONUSA`,
    description: p.seo_description || p.short_description,
    canonical: CANONICAL_BASE + "/product/" + p.slug,
    image: mediaUrl(p.images?.[0]),
    jsonLd: {
      "@context": "https://schema.org", "@type": "Product", name: p.name,
      image: p.images?.map(mediaUrl), description: p.short_description, sku: p.sku,
      brand: { "@type": "Brand", name: "ONUSA" },
      offers: { "@type": "Offer", priceCurrency: "EUR", price: (p.sale_price || p.price).toFixed(2),
        availability: (p.stock > 0) ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        url: CANONICAL_BASE + "/product/" + p.slug },
      ...(p.review_count ? { aggregateRating: { "@type": "AggregateRating", ratingValue: p.rating, reviewCount: p.review_count } } : {}),
    },
  } : {});

  if (nf) return <div className="onusa-container py-32 text-center"><p className="heading-x text-2xl">PRODUCT NIET BESCHIKBAAR</p><Link to="/shop" className="btn-x-outline mt-6">TERUG NAAR SHOP</Link></div>;
  if (!p) return <div className="onusa-container py-32"><div className="grid md:grid-cols-2 gap-10"><div className="aspect-[3/4] bg-[#E8E1D8] animate-pulse" /><div className="space-y-4"><div className="h-8 bg-[#E8E1D8] animate-pulse w-2/3" /><div className="h-4 bg-[#E8E1D8] animate-pulse w-1/3" /></div></div></div>;

  const price = p.sale_price || p.price;
  const onSale = p.sale_price && p.sale_price < p.price;
  const availableSizes = p.variants?.filter((v) => v.stock > 0).map((v) => v.size) || p.sizes;
  const outOfStock = (p.variants?.length ? p.variants.every((v) => v.stock <= 0) : p.stock <= 0);
  const colors = p.colors || [];
  const gallery = (color && p.variant_images?.[color]?.length) ? p.variant_images[color] : (p.images || []);
  const freeThreshold = settings?.free_shipping_threshold ?? 150;
  const sp = settings?.social_proof;
  const flatRate = settings?.shipping_flat_rate ?? 6.95;
  const sizeOptions = p.sizes?.length ? p.sizes : (p.variants?.map((v) => v.size) || []);

  const pickColor = (c) => { setColor(c); setActive(0); };

  const add = () => {
    if (sizeOptions.length && !size) return toast.error("Kies een maat");
    if (colors.length && !color) return toast.error("Kies een kleur");
    addItem({ product_id: p.id, slug: p.slug, name: p.name, image: gallery?.[0] || p.images?.[0], color: color || p.color, size, price, max: 10 });
    toast.success("Toegevoegd aan winkelmand");
  };

  const quality = [["Materiaal", p.material], ["Gewicht", p.weight], ["Samenstelling", p.composition], ["Afwerking", p.care]].filter(([, v]) => v);

  return (
    <div data-testid="product-page">
      <div className="onusa-container py-8 md:py-12 grid md:grid-cols-2 gap-8 lg:gap-16">
        {/* GALLERY */}
        <div>
          <button className="block w-full aspect-[3/4] bg-[#E8E1D8] overflow-hidden cursor-zoom-in" onClick={() => setLightbox(active)} data-testid="product-main-image">
            <img src={mediaUrl(gallery?.[active] || gallery?.[0])} alt={p.alt_text || p.name} className="w-full h-full object-cover" />
          </button>
          {gallery?.length > 1 && (
            <div className="grid grid-cols-5 gap-2 mt-2">
              {gallery.map((im, i) => (
                <button key={i} onClick={() => setActive(i)} className={`aspect-square bg-[#E8E1D8] overflow-hidden border ${active === i ? "border-[#1D1D1B]" : "border-transparent"}`} data-testid={`product-thumb-${i}`}>
                  <img src={mediaUrl(im)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* INFO */}
        <div className="md:pt-2">
          {p.collection && <Link to={`/collections/${p.collection}`} className="label-x hover:opacity-60">{p.collection.toUpperCase()}</Link>}
          <h1 className="heading-x text-3xl md:text-4xl mt-2" data-testid="product-name">{p.name}</h1>
          <div className="flex items-center gap-3 mt-3">
            <span className="text-2xl md:text-3xl font-semibold tracking-tight" data-testid="product-price">€{price.toFixed(2)}</span>
            {onSale && <span className="font-mono text-[#77736D] line-through">€{p.price.toFixed(2)}</span>}
            {onSale && <span className="label-x bg-[#1D1D1B] text-[#F7F5F1] px-2 py-0.5">-{Math.round((1 - price / p.price) * 100)}%</span>}
            {p.rating && <a href="#reviews" className="flex items-center gap-1 label-x ml-auto"><Star size={13} fill="#1D1D1B" /> {p.rating} ({p.review_count})</a>}
          </div>
          {sp?.enabled && (
            <div className="flex items-center gap-2 mt-3" data-testid="social-proof">
              <div className="flex">{[0, 1, 2, 3, 4].map((i) => <Star key={i} size={16} className="text-[#1D1D1B]" fill={i < Math.round(sp.rating || 5) ? "#1D1D1B" : "none"} />)}</div>
              <span className="text-sm font-semibold">{(sp.rating ?? 5).toString().replace(".", ",")}</span>
              {sp.count && <span className="text-sm text-[#77736D]">· {sp.count} {sp.label || "tevreden klanten"}</span>}
            </div>
          )}
          {p.fit_type && <p className="label-x mt-3 inline-block border border-[#E8E1D8] px-2 py-1" data-testid="product-fittype">{p.fit_type.toUpperCase()}</p>}
          <p className="mt-4 text-[#77736D] leading-relaxed">{p.short_description}</p>

          {/* COLORS */}
          {colors.length > 0 && (
            <div className="mt-7">
              <p className="label-x mb-2">KLEUR{color ? `: ${color}` : ""}</p>
              <div className="flex flex-wrap gap-2" data-testid="product-colors">
                {colors.map((c) => (
                  <button key={c} onClick={() => pickColor(c)} className={`px-3 py-2 text-xs border transition-colors ${color === c ? "bg-[#1D1D1B] text-[#F7F5F1] border-[#1D1D1B]" : "border-[#E8E1D8] hover:border-[#1D1D1B]"}`} data-testid={`color-${c}`}>{c}</button>
                ))}
              </div>
            </div>
          )}

          {/* SIZE */}
          {sizeOptions.length > 0 && (
            <div className="mt-7">
              <div className="flex items-center justify-between mb-2">
                <p className="label-x">MAAT</p>
                {(p.size_guide?.length > 0 || p.model_height) && (
                  <button onClick={() => setGuideOpen(true)} className="label-x flex items-center gap-1 hover:opacity-60" data-testid="open-size-guide"><Ruler size={13} /> MAATTABEL</button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {sizeOptions.map((s) => {
                  const avail = availableSizes.includes(s);
                  return (
                    <button key={s} disabled={!avail} onClick={() => setSize(s)}
                      className={`min-w-12 px-3 py-2.5 text-xs font-mono border transition-colors ${size === s ? "bg-[#1D1D1B] text-[#F7F5F1] border-[#1D1D1B]" : "border-[#E8E1D8] hover:border-[#1D1D1B]"} ${!avail ? "opacity-30 line-through cursor-not-allowed" : ""}`}
                      data-testid={`size-${s}`}>{s}</button>
                  );
                })}
              </div>
              {(p.model_height || p.model_size) && (
                <p className="text-xs text-[#77736D] mt-2" data-testid="model-fit-hint">
                  Model{p.model_height ? ` is ${p.model_height}` : ""}{p.model_size ? ` en draagt maat ${p.model_size}` : ""}.
                  {p.fit_type ? " Twijfel je tussen twee maten? Kies bij een oversized/relaxed fit de kleinere, bij regular/slim de grotere." : ""}
                </p>
              )}
            </div>
          )}

          <button onClick={add} disabled={outOfStock} className="btn-x w-full mt-7" data-testid="add-to-cart-btn">
            {outOfStock ? "UITVERKOCHT" : "IN WINKELMAND"}
          </button>

          {/* TRUST / SHIPPING QUICK */}
          <div className="mt-5 space-y-2 text-xs text-[#77736D]" data-testid="trust-block">
            <p className="flex items-center gap-2"><Truck size={14} /> Gratis verzending vanaf €{freeThreshold.toFixed?.(0) || freeThreshold} · anders €{Number(flatRate).toFixed(2)}. Verzending binnen 1–2 werkdagen.</p>
            <p className="flex items-center gap-2"><RotateCcw size={14} /> 14 dagen retourrecht op ongedragen artikelen. <Link to="/returns" className="underline">Retourbeleid</Link></p>
            <p className="flex items-center gap-2"><ShieldCheck size={14} /> Veilig betalen via iDEAL, creditcard &amp; meer. <Link to="/contact" className="underline">Vragen? Contact</Link></p>
          </div>

          <Accordion type="single" collapsible className="mt-8">
            {p.description && (
              <AccordionItem value="details" className="border-[#E8E1D8]">
                <AccordionTrigger className="label-x hover:no-underline" data-testid="accordion-details">DETAILS</AccordionTrigger>
                <AccordionContent className="text-sm text-[#77736D] leading-relaxed">{p.description}</AccordionContent>
              </AccordionItem>
            )}
            {quality.length > 0 && (
              <AccordionItem value="quality" className="border-[#E8E1D8]">
                <AccordionTrigger className="label-x hover:no-underline" data-testid="accordion-quality">MATERIAAL &amp; KWALITEIT</AccordionTrigger>
                <AccordionContent className="text-sm text-[#77736D] leading-relaxed">
                  <dl className="space-y-1">{quality.map(([k, v]) => <div key={k} className="flex gap-2"><dt className="w-32 shrink-0 text-[#1D1D1B]">{k}</dt><dd>{v}</dd></div>)}</dl>
                </AccordionContent>
              </AccordionItem>
            )}
            {(p.fit || p.fit_type) && (
              <AccordionItem value="fit" className="border-[#E8E1D8]">
                <AccordionTrigger className="label-x hover:no-underline" data-testid="accordion-fit">PASVORM</AccordionTrigger>
                <AccordionContent className="text-sm text-[#77736D] leading-relaxed">{p.fit_type ? `${p.fit_type}. ` : ""}{p.fit || ""}</AccordionContent>
              </AccordionItem>
            )}
            <AccordionItem value="shipping" className="border-[#E8E1D8]">
              <AccordionTrigger className="label-x hover:no-underline" data-testid="accordion-shipping">VERZENDING</AccordionTrigger>
              <AccordionContent className="text-sm text-[#77736D] leading-relaxed">
                Verzending binnen 1–2 werkdagen door heel Europa. Gratis vanaf €{freeThreshold.toFixed?.(0) || freeThreshold}, anders €{Number(flatRate).toFixed(2)}. <Link to="/shipping" className="underline">Meer over verzending</Link>.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="returns" className="border-[#E8E1D8]">
              <AccordionTrigger className="label-x hover:no-underline" data-testid="accordion-returns">RETOUREN</AccordionTrigger>
              <AccordionContent className="text-sm text-[#77736D] leading-relaxed">
                14 dagen retourrecht op ongedragen artikelen met kaartje. <Link to="/returns" className="underline">Volledig retourbeleid</Link>.
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <p className="label-x mt-8 text-[#77736D]" data-testid="brand-line">DISCIPLINE CREATES FREEDOM</p>
        </div>
      </div>

      {p.lookbook_blocks?.length > 0 && (
        <div className="onusa-container py-12 space-y-6" data-testid="product-lookbook">
          {p.lookbook_blocks.map((b, i) => (
            <div key={i} className="space-y-3">
              {b.image && <div className="w-full overflow-hidden bg-[#E8E1D8]"><img src={mediaUrl(b.image)} alt={b.caption || ""} className="w-full object-cover" /></div>}
              {b.heading && <h3 className="heading-x text-2xl">{b.heading}</h3>}
              {b.caption && <p className="label-x text-center">{b.caption}</p>}
              {b.body && <p className="text-[#77736D]">{b.body}</p>}
            </div>
          ))}
        </div>
      )}

      <div id="reviews" className="onusa-container py-12 border-t border-[#E8E1D8]" data-testid="product-reviews">
        <h2 className="heading-x text-2xl mb-6">REVIEWS</h2>
        {p.reviews?.length > 0 ? (
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {p.reviews.map((r) => (
              <div key={r.id} className="border border-[#E8E1D8] p-5 bg-white">
                <div className="flex items-center gap-2">
                  <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} size={13} fill={i < r.rating ? "#1D1D1B" : "none"} />)}</div>
                  {r.verified_purchase && <span className="label-x">GEVERIFIEERD</span>}
                </div>
                <p className="text-sm font-medium mt-2 uppercase tracking-wide">{r.title}</p>
                <p className="text-sm text-[#77736D] mt-1">{r.text}</p>
                <p className="label-x mt-2">{r.name}</p>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-[#77736D] mb-8">Nog geen reviews. Wees de eerste.</p>}
        <ReviewForm productId={p.id} onDone={load} />
      </div>

      {p.related?.length > 0 && (
        <div className="onusa-container py-12 border-t border-[#E8E1D8]">
          <h2 className="heading-x text-2xl mb-8">GERELATEERDE PRODUCTEN</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
            {p.related.map((rp) => <ProductCard key={rp.id} product={rp} />)}
          </div>
        </div>
      )}

      {/* STICKY MOBILE ADD-TO-CART */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[#F7F5F1]/95 backdrop-blur border-t border-[#E8E1D8] px-4 py-3 flex items-center gap-3" data-testid="sticky-add-bar">
        <div className="min-w-0">
          <p className="text-base font-semibold">€{price.toFixed(2)}</p>
          <p className="label-x truncate">{size ? `Maat ${size}` : "Kies maat"}{color ? ` · ${color}` : ""}</p>
        </div>
        <button onClick={add} disabled={outOfStock} className="btn-x flex-1" data-testid="sticky-add-btn">{outOfStock ? "UITVERKOCHT" : "IN WINKELMAND"}</button>
      </div>
      <div className="md:hidden h-20" />

      {/* SIZE GUIDE DIALOG */}
      <Dialog open={guideOpen} onOpenChange={setGuideOpen}>
        <DialogContent className="max-w-md bg-[#F7F5F1]" data-testid="size-guide-dialog">
          <DialogHeader><DialogTitle className="heading-x text-sm">MAATTABEL</DialogTitle></DialogHeader>
          {p.size_guide?.length > 0 ? (
            <table className="w-full text-sm">
              <thead><tr className="border-b border-[#E8E1D8] text-left label-x"><th className="py-2">MAAT</th><th className="py-2">BORST (CM)</th><th className="py-2">LENGTE (CM)</th></tr></thead>
              <tbody>{p.size_guide.map((r, i) => <tr key={i} className="border-b border-[#E8E1D8]"><td className="py-2 font-mono">{r.size}</td><td className="py-2">{r.chest || "—"}</td><td className="py-2">{r.length || "—"}</td></tr>)}</tbody>
            </table>
          ) : <p className="text-sm text-[#77736D]">Geen maattabel beschikbaar.</p>}
          {(p.model_height || p.model_size) && <p className="text-xs text-[#77736D] mt-3">Model{p.model_height ? ` is ${p.model_height}` : ""}{p.model_size ? ` en draagt maat ${p.model_size}` : ""}.</p>}
        </DialogContent>
      </Dialog>

      {/* LIGHTBOX */}
      {lightbox !== null && (
        <div className="fixed inset-0 z-50 bg-black/92 flex items-center justify-center" onClick={() => setLightbox(null)} data-testid="lightbox">
          <button className="absolute top-4 right-4 text-white/80 hover:text-white" onClick={() => setLightbox(null)} data-testid="lightbox-close"><X size={28} /></button>
          {gallery.length > 1 && <button className="absolute left-4 text-white/80 hover:text-white" onClick={(e) => { e.stopPropagation(); setLightbox((l) => (l - 1 + gallery.length) % gallery.length); }}><ChevronLeft size={36} /></button>}
          <img src={mediaUrl(gallery[lightbox])} alt="" className="max-h-[88vh] max-w-[92vw] object-contain" onClick={(e) => e.stopPropagation()} />
          {gallery.length > 1 && <button className="absolute right-4 text-white/80 hover:text-white" onClick={(e) => { e.stopPropagation(); setLightbox((l) => (l + 1) % gallery.length); }}><ChevronRight size={36} /></button>}
        </div>
      )}
    </div>
  );
}
